import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Upload, CheckCircle, AlertCircle, Eye, Save, Trash2, History, FileSpreadsheet } from 'lucide-react';
import axios from 'axios';
import { API } from '../utils/config';


const ExcelImporter = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [importType, setImportType] = useState('properties'); // properties, partners, property_managers, bookings
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [importLogs, setImportLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const importTypes = [
    { value: 'properties', label: '🏠 Immobili', description: 'Importa proprietà da Excel' },
    { value: 'partners', label: '🤝 Partner', description: 'Importa partner da Excel' },
    { value: 'property_managers', label: '👥 Responsabili', description: 'Importa responsabili da Excel' },
    { value: 'bookings', label: '🎫 Prenotazioni', description: 'Importa prenotazioni da Excel' },
    { value: 'closures', label: '🚫 Chiusure', description: 'Importa periodi di chiusura immobili' },
    { value: 'cir_update', label: '🔄 Aggiorna CIR', description: 'Aggiorna CIR degli immobili esistenti' }
  ];

  // Carica i log degli import quando cambia il tipo
  useEffect(() => {
    loadImportLogs();
  }, [importType]);

  const loadImportLogs = async () => {
    setLoadingLogs(true);
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(
        `${API}/api/admin/import-logs?import_type=${importType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setImportLogs(response.data);
    } catch (err) {
      console.error('Errore caricamento log:', err);
    }
    setLoadingLogs(false);
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
    setPreviewData(null);
    setResult(null);
    setError(null);
  };

  const handleUploadAndPreview = async () => {
    if (!selectedFile) {
      setError('Seleziona un file Excel prima');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('import_type', importType);

      const token = localStorage.getItem('hw_token');
      const response = await axios.post(
        `${API}/api/admin/excel-preview`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setPreviewData(response.data);
      setUploading(false);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || 'Errore durante il caricamento del file');
      setUploading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!previewData) return;

    if (!window.confirm(`Confermi l'importazione di ${previewData.records.length} record?`)) {
      return;
    }

    setImporting(true);
    setError(null);

    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(
        `${API}/api/admin/excel-import-confirm`,
        {
          import_type: importType,
          records: previewData.records,
          file_name: selectedFile?.name || 'unknown'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResult(response.data);
      setImporting(false);
      // Ricarica i log dopo l'import
      loadImportLogs();
      setPreviewData(null);
      setSelectedFile(null);
    } catch (err) {
      console.error('Import error:', err);
      setError(err.response?.data?.detail || 'Errore durante l\'importazione');
      setImporting(false);
    }
  };

  const handleEditCell = (rowIndex, field, value) => {
    const newData = { ...previewData };
    newData.records[rowIndex][field] = value;
    setPreviewData(newData);
  };

  const handleDeleteRow = (rowIndex) => {
    const newData = { ...previewData };
    newData.records.splice(rowIndex, 1);
    setPreviewData(newData);
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">📊 Import da Excel</h1>

        {/* Step 1: Selezione Tipo e File */}
        {!previewData && (
          <Card className="p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Step 1: Seleziona Tipo Import e File</h2>

            {/* Tipo Import */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-3">Cosa vuoi importare?</label>
              <div className="grid grid-cols-2 gap-4">
                {importTypes.map((type) => (
                  <div
                    key={type.value}
                    onClick={() => setImportType(type.value)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition ${
                      importType === type.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="font-semibold text-lg mb-1">{type.label}</div>
                    <div className="text-sm text-gray-600">{type.description}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* File Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Carica File Excel</label>
              <Input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                className="cursor-pointer"
              />
              {selectedFile && (
                <p className="text-sm text-green-600 mt-2">
                  ✅ File selezionato: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                </p>
              )}
            </div>

            {/* Upload Button */}
            <Button
              onClick={handleUploadAndPreview}
              disabled={!selectedFile || uploading}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {uploading ? (
                <>
                  <Upload className="w-5 h-5 mr-2 animate-spin" />
                  Caricamento in corso...
                </>
              ) : (
                <>
                  <Eye className="w-5 h-5 mr-2" />
                  Carica e Visualizza Anteprima
                </>
              )}
            </Button>
          </Card>
        )}

        {/* Step 2: Preview e Modifica */}
        {previewData && !result && (
          <Card className="p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                Step 2: Verifica e Modifica Dati ({previewData.records.length} record)
              </h2>
              <div className="space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setPreviewData(null)}
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleConfirmImport}
                  disabled={importing}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {importing ? (
                    'Importazione...'
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Conferma Import
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Info */}
            {previewData.info && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">{previewData.info}</p>
              </div>
            )}

            {/* Table Preview */}
            <div className="overflow-x-auto max-h-96 overflow-y-auto border rounded">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    {previewData.columns.map((col) => (
                      <th key={col} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        {col}
                      </th>
                    ))}
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Azioni</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {previewData.records.map((record, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm">{idx + 1}</td>
                      {previewData.columns.map((col) => (
                        <td key={col} className="px-3 py-2">
                          <input
                            type="text"
                            value={record[col] || ''}
                            onChange={(e) => handleEditCell(idx, col, e.target.value)}
                            className="w-full px-2 py-1 text-sm border rounded"
                          />
                        </td>
                      ))}
                      <td className="px-3 py-2">
                        <button
                          onClick={() => handleDeleteRow(idx)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Step 3: Result */}
        {result && (
          <Card className="p-6 border-green-200 bg-green-50">
            <div className="flex items-start gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-green-900 text-lg mb-2">
                  ✅ Import Completato con Successo!
                </h3>
                <p className="text-green-800">
                  {result.imported} record importati correttamente
                </p>
              </div>
            </div>

            <Button
              onClick={() => {
                setResult(null);
                setSelectedFile(null);
              }}
              className="mt-4"
            >
              Importa Altro File
            </Button>
          </Card>
        )}

        {/* Error */}
        {error && (
          <Card className="p-6 border-red-200 bg-red-50">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-red-900 text-lg mb-2">❌ Errore</h3>
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Storico Import */}
        <Card className="p-6 mt-6">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold">
              Storico Import: {importTypes.find(t => t.value === importType)?.label || importType}
            </h2>
          </div>
          
          {loadingLogs ? (
            <p className="text-gray-500">Caricamento...</p>
          ) : importLogs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileSpreadsheet className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nessun import effettuato per questa categoria</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">File</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Creati</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aggiornati</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Saltati</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Totale</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Importato da</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {importLogs.map((log, idx) => (
                    <tr key={log.id || idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {new Date(log.imported_at).toLocaleString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono">
                        {log.file_name || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                          +{log.created_count}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                          ↻{log.updated_count}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                          ⏭{log.skipped_count}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-gray-900">
                        {log.total_processed}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {log.imported_by}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          <Button
            variant="outline"
            onClick={loadImportLogs}
            className="mt-4"
            disabled={loadingLogs}
          >
            🔄 Aggiorna Storico
          </Button>
        </Card>
      </div>
    </Layout>
  );
};

export default ExcelImporter;
